import { BtcAddressFormat, getAddressFormatFromAddress } from '@optimex-xyz/trade-wallet-sdk'

import { useSettings } from '@/store'
import { INetwork, IToken } from '@/types'
import { getBitcoinBalance } from '@/utils/balance'
import { isBtcChain } from '@/utils/chain'
import { WALLET_PHANTOM, WALLET_XVERSE } from '@/utils/constants'
import {
  AddressResponse,
  BtcAccount,
  IWallet,
  IWalletConnectOptions,
  PsbtSignOptions,
  TransactionOptions,
  WalletContextType,
} from '@/wallets'

export default async function handleConnectWallet({
  network,
  options,
  selectedWallet,
  disconnect,
  onChangeAccount,
}: {
  onChangeAccount: (data: WalletContextType) => void
  network: string
  options: IWalletConnectOptions
  selectedWallet: IWallet
  disconnect: () => Promise<void>
}) {
  const formatContextState = async ({ address, networkInfo }: { address: string; networkInfo: INetwork }) => {
    const newWalletContext: WalletContextType = {
      wallet: selectedWallet,
      address,
      isConnected: true,
      network,
      networkInfo,
      connect: async () => {},
      disconnect,
      sendTransaction: (toAddress: string, amount: number | bigint, network: string, options?: TransactionOptions) =>
        selectedWallet.sendTransaction(toAddress, amount, network, options),
      signMessage: (message: string) => selectedWallet.signMessage(message, network),
      signPsbt: (psbt: string, network: string, options?: PsbtSignOptions) =>
        selectedWallet.signPsbt(psbt, network, options),
      getCompressedPublicKey: () => selectedWallet.getCompressedPublicKey(network),
      connectedAt: Date.now(),
    }
    return newWalletContext
  }
  const connectParams = {
    ...options,
    onAccountChanged: async (data: { address: string; networkInfo: INetwork }) => {
      console.debug('changed account', data)
      onChangeAccount(await formatContextState(data))
    },
    onDisconnected: disconnect,
  }

  const { address, networkInfo } = await selectedWallet.connect(network, connectParams)

  const newState = await formatContextState({ address, networkInfo })
  onChangeAccount(newState)
  return newState
}

export const isSupportSwap = ({ wallet, fromToken }: { wallet: WalletContextType; fromToken: IToken | null }) => {
  // xverse taproot not support swap
  const isXverseTaproot =
    isBtcChain(fromToken?.networkId) &&
    wallet?.isConnected &&
    wallet?.wallet?.key === WALLET_XVERSE &&
    getAddressFormatFromAddress(wallet?.address) === BtcAddressFormat.P2TR // taproot
  return !isXverseTaproot
}

export const isSupportResubmitSignature = ({
  wallet,
  fromToken,
}: {
  wallet: WalletContextType
  fromToken: IToken | null
}) => {
  const isPhantomTaproot =
    isBtcChain(fromToken?.networkId) &&
    wallet?.isConnected &&
    wallet?.wallet?.key === WALLET_PHANTOM &&
    getAddressFormatFromAddress(wallet?.address) === BtcAddressFormat.P2TR // taproot
  // dont support because sign message on taproot randomize
  return !isPhantomTaproot
}

async function getBalance(networkId: string, walletAddress: string): Promise<string> {
  if (isBtcChain(networkId)) {
    return (await getBitcoinBalance(networkId, walletAddress)).toString()
  }
  return '0'
}
export const getPreferredBtcAccount = async (accounts: BtcAccount[] | AddressResponse[], networkId: string) => {
  const userType = useSettings.getState().btcAddressType

  if (userType) {
    const account = accounts.find((acc) => getAddressFormatFromAddress(acc.address) === userType)
    if (account) return account
  }

  const nativeSegwit = accounts.find((acc) => getAddressFormatFromAddress(acc.address) === BtcAddressFormat.P2WPKH)

  let account
  try {
    const balances = await Promise.all(accounts.map((acc) => getBalance(networkId, acc.address)))
    if (balances.every((e) => !!+e)) account = nativeSegwit
    else {
      const accountHasBalanceIndex = balances.findIndex((el) => !!+el)
      account = accounts[accountHasBalanceIndex]
    }
  } catch {}
  return account || nativeSegwit || accounts[0]
}
